import React, {FC, useState} from 'react';
import {Col, FormListFieldData, Row} from "antd";
import {MinusCircleOutlined} from "@ant-design/icons";
import {CustomDatePicker} from '../../../../../components/UI/AntCustom/CustomDatePicker';
import {DateAndTimePicker} from '../../../../../components/UI/AntCustom/DateAndTimePicker';
import {fetchAirports} from "../../../../../utils/helpers/selectDataFetchers/fetchAirports";
import {DebounceSelect} from "../../../../../components/UI/AntCustom/DebounceSelect";
import {useAirportFilter} from "../../../../../utils/hooks/useAirportFilter";
import {isDateLessTanOneMinuteAgo} from "../../../../../utils/helpers/dateHelpers/scheduleDateHelpers";
import {defineMessages, useIntl} from "react-intl";
import {CommonFormMessages} from "../../../../../intl/commonMessages/CommonFormMessages";
import {CustomFieldInput} from "../../../../../components/UI/AntCustom/CustomFieldInput";
import styled from "styled-components";
import {CustomInputNumber} from "../../../../../components/UI/AntCustom/CustomInputNumber";
import {useIsMobile} from "../../../../../hooks/useWindowSize";
import {getDateAndTimePickerValidationRules} from "../../../../../utils/helpers/formHelpers/getDateAndTimePickerValidationRules";
import {Dayjs} from "dayjs";
import {Form} from "antd";

const TripItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
`;

const TripItemTitle = styled.div`
  display: flex;
  justify-content: space-between;
  font-weight: 500;
  font-size: 18px;
  line-height: 22px;
  color: #000000;
`;

const TripItemSeparator = styled.div`
  width: 100%;
  height: 0;
  border: 1px dashed #D9D9D9;
  margin: 8px 0 16px 0;
`;


const TripItemMessages = defineMessages({
  title: {
    id: "tripItem.title",
    defaultMessage: "Flight",
  },
})

interface TripItemProps {
  field?: FormListFieldData;
  onRemoveClick?: () => void;
  routeNumber: number;
  form: any;
}

export const TripItem: FC<TripItemProps> = ({field, onRemoveClick, routeNumber, form}) => {
  const intl = useIntl();
  const isMobile = useIsMobile();

  const fromAirportFilter = useAirportFilter(form, field ? ['routes', field.name, 'airportToId'] : 'airportToId');
  const toAirportFilter = useAirportFilter(form, field ? ['routes', field.name, 'airportFromId'] : 'airportFromId');
  
  // Get all routes to access previous leg's date
  const routes = Form.useWatch('routes', form) || [];
  const previousLegIndex = routeNumber - 2; // routeNumber is 1-based, array is 0-based, so -2 to get previous
  
  // Get previous leg's date for validation
  const previousLegDate = previousLegIndex >= 0 && routes[previousLegIndex]?.departureDate 
    ? routes[previousLegIndex].departureDate 
    : null;
  
  // Function to disable dates before previous leg's date
  const getDisabledDate = (current: Dayjs) => {
    // First check the standard validation (not in the past)
    if (isDateLessTanOneMinuteAgo(current)) {
      return true;
    }
    
    // If there's a previous leg with a date, disable dates before it
    if (previousLegDate) {
      // Compare dates at day level (ignore time)
      return current.isBefore(previousLegDate, 'day');
    }
    
    return false;
  };
  
  // Validation rule to ensure date is >= previous leg's date
  const dateValidationRules = [
    ...getDateAndTimePickerValidationRules(intl, true),
    {
      validator: (_: any, value: Dayjs | null) => {
        if (!value || !previousLegDate) {
          return Promise.resolve();
        }
        
        // Check if the date is before the previous leg's date (at day level)
        if (value.isBefore(previousLegDate, 'day')) {
          const previousDateStr = previousLegDate.format('YYYY-MM-DD');
          return Promise.reject(new Error(
            `Date must be on or after ${previousDateStr} (previous leg's date)`
          ));
        }
        
        return Promise.resolve();
      }
    }
  ];

  return (
    <>
      <TripItemWrapper>
        <TripItemTitle>
          <div>{intl.formatMessage(TripItemMessages.title)} {routeNumber}</div>
          {routeNumber > 2 && <MinusCircleOutlined onClick={onRemoveClick}/>}
        </TripItemTitle>

        <Row gutter={8}>
          <Col span={isMobile ? 24 : 7}>
            <CustomFieldInput
              {...field}
              label={intl.formatMessage(CommonFormMessages.fromAirportLabel)}
              name={field ? [field.name, 'airportFromId'] : 'airportFromId'}
              rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}>
              <DebounceSelect
                key={fromAirportFilter.key}
                fetchOptions={fromAirportFilter.fetchOptions}
                placeholder={intl.formatMessage(CommonFormMessages.fromAirportPlaceholder)}
                showSearch={true}
              />
            </CustomFieldInput>
          </Col>
          <Col span={isMobile ? 24 : 7}>
            <CustomFieldInput
              {...field}
              label={intl.formatMessage(CommonFormMessages.toAirportLabel)}
              name={field ? [field.name, 'airportToId'] : 'airportToId'}
              rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}>
              <DebounceSelect
                key={toAirportFilter.key}
                fetchOptions={toAirportFilter.fetchOptions}
                placeholder={intl.formatMessage(CommonFormMessages.toAirportPlaceholder)}
                showSearch={true}
              />
            </CustomFieldInput>
          </Col>
          <Col span={isMobile ? 16 : 7}>
            <CustomFieldInput
              {...field}
              label={`${intl.formatMessage(CommonFormMessages.dateLabel)} (UTC)`}
              name={field ? [field.name, 'departureDate'] : 'departureDate'}
              rules={dateValidationRules}>
              <DateAndTimePicker
                format="YYYY-MM-DD"
                placeholder={intl.formatMessage(CommonFormMessages.datePlaceholder)}
                disabledDate={getDisabledDate}
                inputReadOnly={true} placement={"bottomLeft"}
                getPopupContainer={trigger => trigger.parentNode as HTMLElement}
              />
            </CustomFieldInput>
          </Col>
          <Col span={isMobile ? 8 : 3}>
            <CustomFieldInput
              {...field}
              label={intl.formatMessage(CommonFormMessages.paxLabel)}
              name={field ? [field.name, 'pax'] : 'pax'}
              rules={[
                {required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)},
                {min: 1, type: "number", message: intl.formatMessage(CommonFormMessages.ruleMinPax)},
                {max: 100, type: "number", message: intl.formatMessage(CommonFormMessages.ruleMaxPax)}
              ]}>
              <CustomInputNumber controls={true} placeholder={intl.formatMessage(CommonFormMessages.paxPlaceholder)}
                                 style={{width: "100%", height: "48px"}}/>
            </CustomFieldInput>
          </Col>
        </Row>
      </TripItemWrapper>

      <TripItemSeparator/>
    </>
  );
};
